import { Link } from 'react-router-dom';
import { Users, Layers, Code2, Sparkles } from 'lucide-react';

/**
 * About page — copy reflects existing learnableskill.com brand voice:
 * "Trainers, designers, and software developers" creating learning experiences.
 * Emphasizes evidence-based practice, credentialed presenters, and immediate
 * application over theory.
 */
export default function About() {
  return (
    <>
      <section className="bg-gradient-to-b from-brand/5 to-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">
            About Learnable
          </p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Practical learning, built by people who do the work.
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            We're trainers, designers, and software developers who combine our crafts
            to create compelling, meaningful learning experiences for counselors,
            driving schools, and helping professionals.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-16 prose prose-slate">
        <h2 className="text-2xl font-bold">Our approach</h2>
        <p className="text-slate-700">
          Every Learnable course is led by credentialed, experienced presenters and
          grounded in current research and evidence-based practice. We pair visually
          engaging presentations with practical summaries designed for immediate
          application — and sustained skill development.
        </p>

        <h2 className="text-2xl font-bold mt-10">What we offer</h2>
        <p className="text-slate-700">
          Our catalog spans counseling, motivational interviewing, telehealth, HIPAA,
          client documentation, micro skills, substance misuse, and driving school
          education. Beyond our paid courses, we publish free tutorials covering
          software walk-throughs, compliance basics, and productivity tips — because
          a stronger field benefits everyone.
        </p>

        <h2 className="text-2xl font-bold mt-10">Custom training</h2>
        <p className="text-slate-700">
          We also deliver customized training programs — virtually or on-site,
          tailored to teams, organizations, or statewide systems. Whether you need
          to draw from our existing courses or build something specific to your
          workforce, we can design and deliver a program that fits.
        </p>
        <p className="text-slate-700">
          For custom-training inquiries, see our <Link to="/contact" className="text-brand">contact page</Link>.
        </p>
      </section>

      <section className="bg-slate-50 py-16 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-10">What sets us apart</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Pillar
              icon={Users}
              title="Credentialed presenters"
              body="Real-world clinicians and practitioners — not actors reading scripts."
            />
            <Pillar
              icon={Layers}
              title="Evidence-based"
              body="Grounded in current research and validated frameworks."
            />
            <Pillar
              icon={Code2}
              title="Built for application"
              body="Visually engaging lessons paired with practical, take-it-to-Monday summaries."
            />
            <Pillar
              icon={Sparkles}
              title="Continuously updated"
              body="Content is refreshed as the field evolves and standards change."
            />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">Have questions?</h2>
        <p className="mt-3 text-slate-600">
          We'd love to hear from you. Reach out about a course, a license for your
          team, or a custom training engagement.
        </p>
        <Link
          to="/contact"
          className="inline-block mt-6 bg-brand text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-dark"
        >
          Contact us
        </Link>
      </section>
    </>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="w-11 h-11 rounded-lg bg-brand/10 text-brand flex items-center justify-center mb-3">
        <Icon size={20} />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-1">{body}</p>
    </div>
  );
}
